import React, { useEffect, useState } from 'react';
import * as validation from '../../lib/validation';
import * as validationUtils from '../../nighthawk/utility/validationUtils';
import * as multiValue from '../../nighthawk/utility/multiValueUtils';
import { NhLoadingIndicatorModal } from '../../nighthawk/common/loadingIndicator/nhLoadingIndicatorModal'
import * as guidedResponseActions from '../../actions/guidedResponseActions';
import { GuidedResponseWorkflow } from './guidedResponseWorkflow';
import { DebugComponent } from './guidedResponseDebugMode';
import { default as DebugErrors } from '../../components/common/activeErrors';
import '../../nighthawk/css/views/guidedResponseView.css';
import LoggerUtils from 'lib/loggerUtils';
import { useAppDispatch, useAppSelector } from '../../store';
import async from 'react-select/dist/declarations/src/async/index';

interface Question {
	elementRef: string;
	inputType: string;
	isMultiValue: boolean;
	answer: string | null;
	altAnswer: string;
	isRequired: boolean;
	conditionalTextTriggerValue: string;
	conditionalTextInputRequired: boolean;
	minLength?: number;
	maxLength?: number;
	dateRestrictionType?: string;
	error?: string | null;
	elementType?: string;
	defaultValue?: string;
	isFocused?: boolean;
	selectOptions?: { value: any }[];
}

type GuidedResponseComponentProps = {
	correlationKey: string;
	productInstanceId: any;
	debugMode?: any;
	patientReferenceId: any;
	careOrderVisitIdentifier: any;
	actions?: any;
	flowId?: any;
	token?: any;
	isLoading: any;
	contextData: any;
	onCompleted: Function;
}



const buildErrorGR = (element: Question, ruleName: string, message: string) => {
	//TODO: double check meaning of isFormLevel. (NH sets to false, but that seems weird)
	//NOTE: ruleName may either go away, or just be set to "element" ... or may be something like "MinValue", "IsRequired", etc ...
	let error = { fieldId: element.elementRef, fieldGroupName: 'guidedResponse', fieldName: element.elementRef, ruleName, message, isFormLevel: true };
	return error;
};

const getErrorLabelByInputType = (inputType:string) : string => {
	let label = "";
	switch (inputType) {
		case "Text":
		case "MultiLineText":
		case "Bool":
			label = "answer";
			break;
		case "Selection":
		case "MultiSelection":
		case "SelectionWithConditionalText":
			label = "selection";
			break;
		case "Date":
			label = "date";
			break;
		case "Integer":
			label = "number";
			break;
		case "PhoneNumber":
			label = "phone number";
			break;
		case "Email":
			label = "email";
			break;
		case "ZipCode":
			label = "zip code";
			break;
		default:
			label = "answer";
			break;
	}
	return label;
}

const validateFieldGR = (element: Question, answer: String, answerIsConditionalText: boolean = false) => {
	let error = null;
	let value = answer; // is Trim appropriate here?
	let hasValue = (value) ? true : false;
	let isAnswerSelection = element.selectOptions?.some((so: any) => so.value === value);

	if (typeof value === 'string' || value instanceof String) {
		value = value.trim();
		hasValue = value.length > 0;
	}

	if (element.inputType === 'SelectionWithConditionalText') {
		if (element.isRequired) {
			if (element.conditionalTextTriggerValue === value && element.conditionalTextInputRequired) {
				value = "";
				hasValue = false;
			}
		}

		if (!error && !answerIsConditionalText && !isAnswerSelection) {
			error = buildErrorGR(
				element,
				validationUtils.COMMON_RULE_NAMES.REQUIRED,
				validationUtils.DEFAULT_ERROR(getErrorLabelByInputType(element.inputType))
			)
		}
	}

	if (element.isRequired) {
		error = (!hasValue) ? buildErrorGR(element, validationUtils.COMMON_RULE_NAMES.REQUIRED, validationUtils.DEFAULT_ERROR(getErrorLabelByInputType(element.inputType))) : null;
	}

	if (element.inputType === "Selection") {
		if (!error && (element.isRequired && !isAnswerSelection)) {
			error = buildErrorGR(
				element,
				validationUtils.COMMON_RULE_NAMES.REQUIRED,
				validationUtils.DEFAULT_ERROR(getErrorLabelByInputType(element.inputType))
			)
		}
	}


	if (!error && !!element.minLength && element.isRequired && !isAnswerSelection && value.length < element.minLength) {
		error = buildErrorGR(element, validationUtils.COMMON_RULE_NAMES.REQUIRED, `Please enter in at least ${element.minLength} characters.`);
	}

	if (!error && !!element.maxLength && element.isRequired && !isAnswerSelection && value.length > element.maxLength) {
		error = buildErrorGR(element, validationUtils.COMMON_RULE_NAMES.REQUIRED, `Only ${element.maxLength} characters are allowed.`);
	}

	if (!error && hasValue && element.dateRestrictionType) {
		let inputRefValid = false;
		switch (element.inputType) {
			case 'Date':
				if (element.dateRestrictionType === "PastOnly") {
					inputRefValid = validation.validatePastDate(value);
				}
				if (element.dateRestrictionType === "FutureOnly") {
					inputRefValid = validation.validateFutureDate(value);
				}
				if (element.dateRestrictionType === "None") {
					inputRefValid = validation.validateAnyDate(value);
				}
				break;
			default:
				inputRefValid = true;
				break;
		}
		if (!inputRefValid) {
			error = buildErrorGR(element, validationUtils.COMMON_RULE_NAMES.REQUIRED, validationUtils.DEFAULT_ERROR(getErrorLabelByInputType(element.inputType)));
		}
	}

	if (element.inputType === "PhoneNumber") {
		if (!error && hasValue && !validation.validatePhoneNumber(value)) {
			error = buildErrorGR(
				element,
				validationUtils.COMMON_RULE_NAMES.REQUIRED,
				validationUtils.DEFAULT_ERROR(getErrorLabelByInputType(element.inputType))
			)
		}
	}

	let errorsResult = [];
	if (error) {
		errorsResult.push(error); // add back in an error for the current element being validated (if still relevant)
	}

	return errorsResult.length ? errorsResult[0] : null;
}

export const validatePageGR = (currentQuestions: Question[], allQuestions: any[]) => {
	currentQuestions.forEach((element: any) => {
		const question = allQuestions.find((question: any) => question.elementRef === element.elementRef);
		const questionIndex = allQuestions.indexOf(question);

		if (element.inputType !== "SelectionWithConditionalText") {
			allQuestions[questionIndex].error = validateFieldGR(element, question.answer);
		} else {
			if (question.answer === question.conditionalTextTriggerValue && question.conditionalTextInputRequired) {
				allQuestions[questionIndex].error = validateFieldGR(element, question.altAnswer, true);
			} else {
				allQuestions[questionIndex].error = validateFieldGR(element, question.answer);
			}
		}
	});

	return allQuestions;
}


const GuidedResponseComponent: React.FC<GuidedResponseComponentProps> = (props) => {

	const dispatch = useAppDispatch();
	const flowSessionResponse = useAppSelector((state) => state.guidedResponse.flowSessionResponse);
	const apiErrors = useAppSelector((state) => state.apiErrors.activeErrors);
	const decisionSupportConfig = useAppSelector((state) => state.config.decisionSupport);
	const questionsFromGuidedResponse = useAppSelector((state) => state.guidedResponse.questions);
	const debugMode = useAppSelector((state) => state.config.decisionSupport.enableFlowTesting || (state.config.decisionSupport.enableURLDebug && state.auth.debugParam))

	const [questions, setQuestions] = useState<Question[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isResumingFlow, setIsResumingFlow] = useState<boolean>(flowSessionResponse?.flowSessionId && flowSessionResponse.flowSessionId !== '00000000-0000-0000-0000-000000000000');


	const isFlowAtStart = flowSessionResponse ? flowSessionResponse.isFlowAtStart : true;
	const isFlowFinished = flowSessionResponse ? flowSessionResponse.isFlowFinished : false;
	useEffect(() => {
		var grFlowSessionIdPresent = (flowSessionResponse?.flowSessionId && flowSessionResponse.flowSessionId !== '00000000-0000-0000-0000-000000000000');

		if (grFlowSessionIdPresent) {
			setIsLoading(true);
			resumeFinishedFlow();
		}
		else {
			startFlow();
		}
	}, [])
	const mapQuestions = (elements: any[]) => {
		if (!elements)
			return;

		let duplicateElementFound = false;
		elements.forEach((element: any) => {
			questions.forEach((question: Question) => {
				if (question.elementRef === element.elementRef)
					duplicateElementFound = true;
			});

			if (!duplicateElementFound) {
				if (element.elementType !== 'Question')
					return;

				const isMultiValue = element.inputType === 'MultiSelection';
				const newQuestion: Question = {
					elementRef: element.elementRef,
					isMultiValue,
					answer: element.defaultValue ? element.defaultValue : '',
					altAnswer: '', //added to support conditional text
					error: null,
					isFocused: false,
					inputType: element.inputType,
					isRequired: element.isRequired,
					conditionalTextTriggerValue: element.conditionalTextTriggerValue,
					conditionalTextInputRequired: element.conditionalTextInputRequired
				}

				setQuestions(prevQuestions => [...prevQuestions, newQuestion]);
			}
		});
	}

	const startFlow = async (debuggerFlowInputs: any[] | null = null, debugPathFileContents: any | null = null) => {

		let startFlowData = {
			correlationKey: props.correlationKey,
			flowId: decisionSupportConfig.flowId,
			flowUniqueToken: null,
			decisionSupportSessionId: null,
			// versionRef: versionRef, //TODO: Decide if we want to bring this feature (debugMode) into RD2
			// effectiveAt: effectiveAt,
			productInstanceId: props.productInstanceId,
			inputFields: props.contextData.inputFields,
			enableDebugging: debugMode,
			debuggerFlowInputs,
			debugPathFileContents,
			patientReferenceId: props.patientReferenceId,
			careOrderVisitIdentifier: props.careOrderVisitIdentifier
		}
		try {
			const response = await dispatch(guidedResponseActions.startFlow(startFlowData))
			if (response && response.payload && response.payload.data && response.payload.data.elementSet)
				mapQuestions(response.payload.data.elementSet?.elements);

			setIsLoading(false);
			window.scrollTo(0, 0);
		}
		catch (err: any) {

		}
	}

	const resumeFlowFromSession = async (flowSessionId: string) => {
		try {
			const response = await dispatch(guidedResponseActions.getSession(flowSessionId))
			if (response && response.payload && response.payload.data && response.payload.data.elementSet)
				mapQuestions(response.payload.data.elementSet?.elements);

			setIsLoading(false);
			window.scrollTo(0, 0);
			return response;
		}
		catch (err: any) {

		}
	}

	const continueFlow = async (flowSessionId: any, userResponses: any) => {
		userResponses.responses.forEach((response:any) => {
			response.responseValue = encodeURIComponent(response.responseValue);
		});
		try {
			const response = await dispatch(guidedResponseActions.continueFlow(flowSessionId, userResponses))
			if (response && response.payload && response.payload.data && response.payload.data.elementSet)
				mapQuestions(response.payload.data.elementSet?.elements);

			setIsLoading(false);
			window.scrollTo(0, 0);
		}
		catch (err: any) {

		}
	}

	const previous = async (flowSessionId:string) => {
		if (flowSessionResponse && flowSessionResponse.elementSet) {
			const { elements } = flowSessionResponse.elementSet;

			const currentQuestions = elements.filter((element:Question) => element.elementType === 'Question');
			const allQuestions = questions;

			currentQuestions.forEach((currentQuestion : Question) => {
				const question = allQuestions.find((question : any)=> question.elementRef === currentQuestion.elementRef)
				if (question && question.inputType === "MultiSelection") {
					question.answer = null;
				}
			});
		}

		try {
			const response = await dispatch(guidedResponseActions.previous(flowSessionId))
			if (response && response.payload && response.payload.data && response.payload.data.elementSet)
				mapQuestions(response.payload.data.elementSet?.elements);

			setIsLoading(false);
			window.scrollTo(0, 0);
			return response;
		}
		catch (err: any) {

		}
	}

	const resumeFinishedFlow = () => {
		//Developer Beware: We MUST make a deep copy  here due to Redux immutability
		//This is to divorce ourselves from Redux state, and prevent mutation between dispatches
		const guidedResponseQuestions = questionsFromGuidedResponse.map((question:any) => {
			return Object.assign({}, question);
		});

		setQuestions(guidedResponseQuestions);

		var grFlowSessionIdPresent = (flowSessionResponse?.flowSessionId && flowSessionResponse.flowSessionId !== '00000000-0000-0000-0000-000000000000');

		if (grFlowSessionIdPresent) {
			if (isFlowFinished && !isFlowAtStart) {
				previous(flowSessionResponse.flowSessionId).then(() => setIsResumingFlow(false));
			} else {
				resumeFlowFromSession(flowSessionResponse.flowSessionId).then(() => setIsResumingFlow(false));
				if (isFlowFinished && isFlowAtStart) {
					handleComplete();
				}
			}
		} else {
			startFlow();
			setIsLoading(false);
			setIsResumingFlow(false);
			window.scrollTo(0, 0);
		}
	}

	const mapFlowSessionResponseToSubpoints = (flowSessionResponse: any) => {
		let results = flowSessionResponse.results.fields;
		let subpoints: any[] = [];

		results.forEach((field: any) => {
			let subpoint = { key: field.fieldName.toLowerCase(), value: field.currentValue };
			subpoints.push(subpoint);
		});

		return subpoints;
	}

	const handleStart = (debuggerFlowInputs: any, debugPathFileContents: any) => {
		startFlow(debuggerFlowInputs, debugPathFileContents);
	}

	const handleNext = (e: any) => {
		e.preventDefault();
		const { elements } = flowSessionResponse.elementSet;
		const currentQuestions = elements.filter((element:Question) => element.elementType === 'Question');
		const allQuestions = questions;

		const responses = currentQuestions.map((currentQuestion: Question) => {
			const question = allQuestions.find((question:any) => question.elementRef === currentQuestion.elementRef)

			if (!question) {
				LoggerUtils.logError('guidedResponseComponent.handleNext allQuestions', allQuestions);
			}

			if (question && question.inputType === "SelectionWithConditionalText") {
				if (question.answer === question.conditionalTextTriggerValue && question.conditionalTextInputRequired) {
					var response = {
						elementRef: question.elementRef,
						responseValue: question.altAnswer
					};
					return response;
				} else {
					return {
						elementRef: question.elementRef,
						responseValue: question.altAnswer !== '' ? question.altAnswer : question.answer
					}
				}

			}

			return {
				elementRef: question?.elementRef,
				responseValue: question?.answer
			}
		});

		let userResponses = {
			flowId: props.flowId,
			elementSetRef: flowSessionResponse && flowSessionResponse.elementSet?.elementSetRef,
			responses,
		};

		const validatedQuestions = validatePageGR(currentQuestions, allQuestions);
		setQuestions([...validatedQuestions]);

		if (!validatedQuestions.find(question => !!question.error))
			continueFlow(flowSessionResponse.flowSessionId, userResponses);
	}

	const handlePrevious = (e:any) => {
		e.preventDefault();

		previous(flowSessionResponse.flowSessionId);
	}

	const handleComplete = async () => {
		//if (props.debugMode) this.setState({ isLoading: true });

		let subpoints = mapFlowSessionResponseToSubpoints(flowSessionResponse);
		await dispatch(guidedResponseActions.saveAnsweredQuestions(questions)); //TODO: This method can go away once new Resume endpoint is implemented
		props.onCompleted(subpoints);
	}

	const handleInputChange = (e: any) => {
		const questionIndex = questions.findIndex((question:any) => question.elementRef === e.target.name);
		let value = e.target.value;
		if (!value && e.options) {
			value = e.options[e.selectedIndex].value;
		}

		const updatedQuestions = [...questions];

		if (!updatedQuestions[questionIndex]) {
			LoggerUtils.logError('guidedResponseComponent.handleInputChange questions', questions);
		}

		if (updatedQuestions[questionIndex].isMultiValue) {
			const answers = updatedQuestions[questionIndex].answer ? multiValue.split(updatedQuestions[questionIndex].answer) : [];
			const toggleAnswer = answers.find((answer:any)=> answer === value);
			updatedQuestions[questionIndex].answer = toggleAnswer ? multiValue.join(answers.filter((answer: any) => answer !== value)) :
				multiValue.join([...answers, value]);
		} else if (updatedQuestions[questionIndex].inputType === "SelectionWithConditionalText") {

			//The answer is coming from the input control.
			if (e.target.className !== 'conditionalTextInput') {
				updatedQuestions[questionIndex].answer = value;
				updatedQuestions[questionIndex].altAnswer = '';
			} else {
				updatedQuestions[questionIndex].altAnswer = value;
			}
		}
		else
			updatedQuestions[questionIndex].answer = value;

		setQuestions(updatedQuestions);
	}

	const handleDropdownChange = (selectedOption: any) => {
		const questionIndex = questions.findIndex((question: any) => question.elementRef === selectedOption.elementRef);
		let value = selectedOption.value;

		const updatedQuestions = questions;

		if (!updatedQuestions[questionIndex]) {
			LoggerUtils.logError('guidedResponseComponent.handleDropdownChange questions', questions);
		}

		updatedQuestions[questionIndex].answer = value;

		setQuestions(updatedQuestions);
	}

	const handleInputFocus = (e: any) => {
		e.preventDefault();
		let updatedQuestions = [...questions];

		const questionIndex = updatedQuestions.findIndex(question => question.elementRef === e.target.name);

		if (!updatedQuestions[questionIndex]) {
			LoggerUtils.logError('guidedResponseComponent.handleInputFocus questions', questions);
		}

		updatedQuestions[questionIndex].isFocused = true;

		setQuestions(updatedQuestions);
	}

	const handleInputBlur = (e: any) => {
		e.preventDefault();
		let updatedQuestions = [...questions];

		const questionIndex = updatedQuestions.findIndex(question => question.elementRef === e.target.name);

		if (!updatedQuestions[questionIndex]) {
			LoggerUtils.logError('guidedResponseComponent.handleInputBlur questions', questions);
		}

		if (updatedQuestions[questionIndex].isFocused) {

			updatedQuestions[questionIndex].isFocused = false;

			setQuestions(updatedQuestions);
		}
	}

	const handleGetDebugPathFile = async (e:any) => {
		e.preventDefault();
		const response = await dispatch(guidedResponseActions.getFlowDebugPathFile(flowSessionResponse?.flowSessionId, props.token));
		if (response && response.payload && response.payload.data) {
			const blob = new Blob([JSON.stringify(response.payload.data)], { type: 'application/json;' });
			let link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = 'FlowDebugPath.json';
			link.click();
			link.remove();
		}
	};

	let className = debugMode ? "view-content decision-support half" : "view-content decision-support";

	if (isLoading || (flowSessionResponse === null && apiErrors?.length === 0)) {
		return <NhLoadingIndicatorModal />
	} else {
		return <>
			{debugMode && <DebugErrors />}
			<div className='guided-response'>
				<GuidedResponseWorkflow
					className={className}
					debugMode={debugMode}
					flowSessionResponse={flowSessionResponse}
					questions={questions}
					isLoading={props.isLoading}
					isFlowAtStart={isFlowAtStart}
					isResumingFlow={isResumingFlow}
					handlers={{
						onDropdownChange: handleDropdownChange,
						onChange: handleInputChange,
						inputBlur: handleInputBlur,
						inputFocus: handleInputFocus,
						start: handleStart,
						next: handleNext,
						previous: handlePrevious,
						complete: handleComplete,
						getFlowDebugPathFile: handleGetDebugPathFile
					}}
				/>
				{debugMode &&
					<DebugComponent
						//debugMode={debugMode}
						flowSessionResponse={flowSessionResponse}
					/>}
			</div>
		</>
	}
}
export default GuidedResponseComponent;